import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Input, 
  List, 
  Avatar, 
  Tag, 
  Typography, 
  Space, 
  Card, 
  Row, 
  Col, 
  Descriptions,
  Empty,
  Spin,
  Button,
  Timeline,
  Statistic,
  Calendar
} from 'antd';
import { 
  SearchOutlined, 
  UserOutlined, 
  MailOutlined, 
  BankOutlined,
  EnvironmentOutlined,
  MedicineBoxOutlined,
  CrownOutlined,
  CalendarOutlined,
  LoginOutlined
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { VIP_STATUS_LABELS, VIP_STATUS_COLORS, VipStatus } from '../types';
import apiClient from '../services/api';

const { Search } = Input;
const { Title, Text } = Typography;

interface SearchUser {
  account_id: string;
  full_name: string;
  email: string;
  clinic_name: string;
  prefecture: string;
  specialty: string;
  vip_status: number;
  created_at: string;
  first_subscription_time?: string;
  last_login_time?: string;
}

interface UserDetail extends SearchUser {
  subscription_product?: string;
  activity_chart?: Array<{
    date: string;
    session_count: number;
    usage_minutes: number;
  }>;
}

interface SearchResponse {
  status: string;
  users: SearchUser[];
}

interface UserDetailResponse {
  status: string;
  user: UserDetail;
}

interface UserSearchModalProps {
  visible: boolean;
  onClose: () => void;
}

const UserSearchModal: React.FC<UserSearchModalProps> = ({ visible, onClose }) => {
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [activityDetailVisible, setActivityDetailVisible] = useState(false);
  const [activityDetailLoading, setActivityDetailLoading] = useState(false);
  const [selectedActivityDate, setSelectedActivityDate] = useState<string>('');
  const [activityDetail, setActivityDetail] = useState<any>(null);
  const [calendarValue, setCalendarValue] = useState<Dayjs>(dayjs());

  // 搜索用户
  const handleSearch = async (value: string) => {
    if (!value.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const response: SearchResponse = await apiClient.get(`/api/analytics/search-users?q=${encodeURIComponent(value.trim())}`);
      setSearchResults(response.users || []);
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  // 获取用户详细信息
  const handleUserSelect = async (user: SearchUser) => {
    setDetailLoading(true);
    try {
      const response: any = await apiClient.get(`/api/analytics/user-detail/${user.account_id}`);
      setSelectedUser(response.user);
      setShowUserDetail(true);
    } catch (error) {
      console.error('Failed to get user detail:', error);
    } finally {
      setDetailLoading(false);
    }
  };

  // 实时搜索
  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearch(searchValue);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchValue]);

  // 处理活动日期点击
  const handleActivityDateClick = async (date: string) => {
    if (!selectedUser) return;
    
    setActivityDetailLoading(true);
    setSelectedActivityDate(date);
    setActivityDetailVisible(true);
    
    try {
      // 使用现有的 analyticsApi.getUserDetail 来获取详细活动
      const response = await apiClient.get(`/api/analytics/user/${selectedUser.account_id}?start_date=${date}&end_date=${date}`);
      setActivityDetail(response);
    } catch (error) {
      console.error('Failed to get activity detail:', error);
    } finally {
      setActivityDetailLoading(false);
    }
  };

  // 关闭Modal时重置状态
  const handleClose = () => {
    setSearchValue('');
    setSearchResults([]);
    setSelectedUser(null);
    setShowUserDetail(false);
    setActivityDetailVisible(false);
    setActivityDetail(null);
    setSelectedActivityDate('');
    setCalendarValue(dayjs());
    onClose();
  };

  // 渲染搜索结果列表
  const renderSearchResults = () => {
    if (loading) {
      return <Spin size="large" style={{ display: 'block', textAlign: 'center', margin: '20px 0' }} />;
    }

    if (!searchValue.trim()) {
      return <Empty description="氏名、メールアドレス、またはクリニック名を入力して検索してください" />;
    }

    if (searchResults.length === 0) {
      return <Empty description="一致するユーザーが見つかりませんでした" />;
    }

    return (
      <List
        dataSource={searchResults}
        renderItem={(user) => (
          <List.Item
            style={{ cursor: 'pointer', padding: '12px' }}
            onClick={() => handleUserSelect(user)}
            className="user-search-item"
          >
            <List.Item.Meta
              avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: VIP_STATUS_COLORS[user.vip_status as VipStatus] }} />}
              title={
                <Space>
                  <span style={{ fontWeight: 'bold' }}>
                    {user.full_name || `ユーザー${user.account_id.slice(-8)}`}
                  </span>
                  <Tag color={VIP_STATUS_COLORS[user.vip_status as VipStatus]} style={{ fontSize: '10px', padding: '0 4px', lineHeight: '16px' }}>
                    {VIP_STATUS_LABELS[user.vip_status as VipStatus]}
                  </Tag>
                </Space>
              }
              description={
                <div>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>
                    <MailOutlined style={{ marginRight: '4px' }} />
                    {user.email}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    <BankOutlined style={{ marginRight: '4px' }} />
                    {user.clinic_name || 'なし'} ({user.prefecture || '不明'})
                  </div>
                </div>
              }
            />
          </List.Item>
        )}
      />
    );
  };

  // 渲染用户详细信息
  const renderUserDetail = () => {
    if (!selectedUser) return null;

    const isPro = selectedUser.vip_status === 1;

    return (
      <div style={{ maxHeight: '65vh', overflowY: 'auto' }}>
        <Row gutter={[12, 12]} style={{ height: '420px' }}>
          {/* 基本情報 */}
          <Col xs={24} lg={12}>
            <Card 
              title="基本情報" 
              size="small"
              style={{ height: '100%' }}
              styles={{ body: { height: 'calc(100% - 57px)', padding: '12px', overflowY: 'auto' } }}
            >
              <Descriptions 
                column={1} 
                size="small" 
                labelStyle={{ marginBottom: '12px', lineHeight: '1.6' }} 
                contentStyle={{ marginBottom: '12px', lineHeight: '1.6' }}
                style={{ lineHeight: '1.8' }}
              >
                <Descriptions.Item label={<><UserOutlined /> 氏名</>} style={{ marginBottom: '12px' }}>
                  <Space>
                    {selectedUser.full_name || `ユーザー${selectedUser.account_id.slice(-8)}`}
                    <Tag color={VIP_STATUS_COLORS[selectedUser.vip_status as VipStatus]}>
                      {VIP_STATUS_LABELS[selectedUser.vip_status as VipStatus]}
                    </Tag>
                  </Space>
                </Descriptions.Item>
                
                <Descriptions.Item label={<><MailOutlined /> メールアドレス</>} style={{ marginBottom: '12px' }}>
                  {selectedUser.email}
                </Descriptions.Item>
                
                <Descriptions.Item label={<><BankOutlined /> クリニック</>} style={{ marginBottom: '12px' }}>
                  {selectedUser.clinic_name || 'なし'}
                </Descriptions.Item>
                
                <Descriptions.Item label={<><EnvironmentOutlined /> 地域</>} style={{ marginBottom: '12px' }}>
                  {selectedUser.prefecture || '不明'}
                </Descriptions.Item>
                
                <Descriptions.Item label={<><MedicineBoxOutlined /> 専門</>} style={{ marginBottom: '12px' }}>
                  {selectedUser.specialty || '不明'}
                </Descriptions.Item>
                
                {selectedUser.subscription_product && (
                  <Descriptions.Item label={<><CrownOutlined /> サブスクリプション</>} style={{ marginBottom: '12px' }}>
                    <Tag color="blue">{selectedUser.subscription_product}</Tag>
                  </Descriptions.Item>
                )}
                
                <Descriptions.Item label={<><CalendarOutlined /> 登録日</>} style={{ marginBottom: '12px' }}>
                  {selectedUser.created_at || '不明'}
                </Descriptions.Item>
                
                {isPro && selectedUser.first_subscription_time && (
                  <Descriptions.Item label={<><CrownOutlined /> 初回課金日</>} style={{ marginBottom: '12px' }}>
                    {selectedUser.first_subscription_time}
                  </Descriptions.Item>
                )}
                
                {selectedUser.last_login_time && (
                  <Descriptions.Item label={<><LoginOutlined /> 最終ログイン</>} style={{ marginBottom: '12px' }}>
                    {new Date(selectedUser.last_login_time).toLocaleString('ja-JP')}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </Col>

          {/* アクティビティカレンダー */}
          <Col xs={24} lg={12}>
            <Card 
              title="アクティビティ分析" 
              size="small"
              style={{ height: '100%' }}
              styles={{ body: { height: 'calc(100% - 57px)', padding: '12px' } }}
            >
              <div style={{ marginBottom: '8px', fontSize: '12px', color: '#ff4d4f' }}>
                赤い日付をクリックして詳細を表示
              </div>
              <div style={{ height: 'calc(100% - 28px)' }}>
                <Calendar
                  fullscreen={false}
                  value={calendarValue}
                  mode="month"
                  onChange={setCalendarValue}
                  headerRender={({ value, type, onChange, onTypeChange }) => {
                    const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];
                    const year = value.year();
                    const month = value.month();
                    
                    return (
                      <div style={{
                        padding: '6px 12px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        borderBottom: '1px solid #f0f0f0',
                        marginBottom: '6px'
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Button
                            size="small"
                            onClick={() => {
                              const newValue = value.clone().year(year - 1);
                              setCalendarValue(newValue);
                              onChange(newValue);
                            }}
                          >
                            {'<<'}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              const newValue = value.clone().month(month - 1);
                              setCalendarValue(newValue);
                              onChange(newValue);
                            }}
                          >
                            {'<'}
                          </Button>
                        </div>
                        
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: 'bold',
                          color: '#262626'
                        }}>
                          {year}年 {months[month]}
                        </div>
                        
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Button
                            size="small"
                            onClick={() => {
                              const newValue = value.clone().month(month + 1);
                              setCalendarValue(newValue);
                              onChange(newValue);
                            }}
                          >
                            {'>'}
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              const newValue = value.clone().year(year + 1);
                              setCalendarValue(newValue);
                              onChange(newValue);
                            }}
                          >
                            {'>>'}
                          </Button>
                        </div>
                      </div>
                    );
                  }}
                  cellRender={(value, info) => {
                    if (info.type === 'date') {
                      const dateStr = value.format('YYYY-MM-DD');
                      const activityData = selectedUser.activity_chart?.find(item => item.date === dateStr);
                      
                      if (activityData) {
                        return (
                          <div style={{ 
                            position: 'absolute',
                            top: '2px',
                            left: '2px',
                            right: '2px',
                            bottom: '2px',
                            border: '2px solid #ff4d4f',
                            borderRadius: '4px',
                            pointerEvents: 'none',
                            backgroundColor: 'transparent'
                          }} />
                        );
                      }
                    }
                    return null;
                  }}
                  onSelect={(date) => {
                    const dateStr = date.format('YYYY-MM-DD');
                    const activityData = selectedUser.activity_chart?.find(item => item.date === dateStr);
                    if (activityData) {
                      handleActivityDateClick(dateStr);
                    }
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  return (
    <>
      {/* 搜索Modal */}
      <Modal
        title={
          <Space>
            <SearchOutlined />
            ユーザー検索
          </Space>
        }
        open={visible && !showUserDetail}
        onCancel={handleClose}
        footer={null}
        width={600}
        style={{ top: 50 }}
      >
        <div style={{ marginBottom: '16px' }}>
          <Search
            placeholder="氏名、メールアドレス、またはクリニック名..."
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            style={{ width: '100%' }}
            allowClear
          />
        </div>
        
        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {renderSearchResults()}
        </div>
      </Modal>

      {/* 用户详细信息Modal */}
      <Modal
        title={
          <Space>
            <UserOutlined />
            ユーザー詳細情報
            {selectedUser && (
              <Tag color={VIP_STATUS_COLORS[selectedUser.vip_status as VipStatus]}>
                {VIP_STATUS_LABELS[selectedUser.vip_status as VipStatus]}
              </Tag>
            )}
          </Space>
        }
        open={showUserDetail}
        onCancel={() => setShowUserDetail(false)}
        footer={[
          <Button key="back" onClick={() => setShowUserDetail(false)}>
            戻る
          </Button>,
          <Button key="close" type="primary" onClick={handleClose}>
            閉じる
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
      >
        <Spin spinning={detailLoading}>
          {renderUserDetail()}
        </Spin>
      </Modal>

      {/* 活動詳細ログ模态框 */}
      <Modal
        title={`活動詳細ログ - ${selectedActivityDate}`}
        open={activityDetailVisible}
        onCancel={() => setActivityDetailVisible(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setActivityDetailVisible(false)}>
            閉じる
          </Button>
        ]}
        width={800}
        style={{ top: 20 }}
      >
        <Spin spinning={activityDetailLoading}>
          {activityDetail && activityDetail.detailed_behaviors ? (
            <div style={{ maxHeight: '400px', overflowY: 'auto', border: '1px solid #f0f0f0', borderRadius: '6px', padding: '12px' }}>
              <div style={{ marginBottom: '16px' }}>
                <Space size="large">
                  <Statistic title="総セッション数" value={activityDetail.stats?.total_sessions || 0} />
                  <Statistic title="総時間" value={`${Math.round((activityDetail.stats?.total_duration || 0) / 60)}分`} />
                  <Statistic title="総操作数" value={activityDetail.stats?.total_behaviors || 0} />
                </Space>
              </div>
              {activityDetail.detailed_behaviors.length > 0 ? (
                <Timeline
                  items={activityDetail.detailed_behaviors.map((behavior: any, index: number) => ({
                    key: index,
                    dot: <span style={{ fontSize: '16px' }}>{behavior.icon || '🔍'}</span>,
                    children: (
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                          <span style={{ fontWeight: 'bold', color: '#1890ff', fontSize: '13px' }}>
                            {new Date(behavior.timestamp).toLocaleString('ja-JP', {
                              month: 'numeric',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                              second: '2-digit'
                            })}
                          </span>
                        </div>
                        <div style={{ marginBottom: '4px' }}>
                          <strong style={{ color: '#333', fontSize: '14px' }}>
                            {behavior.user_action}
                          </strong>
                        </div>
                        <div style={{ color: '#666', fontSize: '12px', marginBottom: '4px' }}>
                          {behavior.operation_intent}
                        </div>
                        {behavior.system_actions && behavior.system_actions.length > 0 && (
                          <div style={{ fontSize: '11px', color: '#999' }}>
                            システム動作: {behavior.system_actions.join(' → ')}
                          </div>
                        )}
                        {behavior.details && Object.keys(behavior.details).length > 0 && (
                          <div style={{ fontSize: '11px', color: '#999', marginTop: '2px' }}>
                            詳細: {JSON.stringify(behavior.details, null, 0)}
                          </div>
                        )}
                      </div>
                    ),
                  }))}
                />
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: 32 }}>
                  この期間に詳細な行動データがありません
                </div>
              )}
            </div>
          ) : (
            <div style={{ textAlign: 'center', color: '#999', padding: 32 }}>
              詳細データを読み込み中...
            </div>
          )}
        </Spin>
      </Modal>

      <style>{`
        .user-search-item:hover {
          background-color: #f5f5f5;
        }
      `}</style>
    </>
  );
};

export default UserSearchModal;